import {
  Box,
  Button,
  Card,
  Dialog,
  DialogTitle,
  DialogActions,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import produce from 'immer';
import moment from 'moment';
import { useState } from 'react';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import useAuth from 'src/hooks/useAuth';
import { useForm } from 'react-hook-form';
import Iconify from 'src/components/Iconify';
import { 
  checkAclValidation, 
  EditContactDetails, 
  getRelatedAcls, 
  ViewContactDetails 
} from 'src/utils/permissions/permission.utils';
import Phone from 'src/sections/crs/common/Phone';
import Email from 'src/sections/crs/common/Email';
import { usePatient } from 'src/@nicheaim/fhir-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider } from '../../../../../components/hook-form';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { PatientWrapper, WrappedPatient } from 'src/@nicheaim/fhir-base/wrappers/Patient';

interface Telecom {
  use: string | undefined;
  value: string | undefined;
  system: string | undefined;
  period: { start: string | undefined; end: string | undefined } | undefined;
  areaCode: string | undefined;
}

type FormValue = {
  email: Telecom[] | undefined;
  phone: Telecom[] | undefined;
};

type EditModeProps = {
  patient: WrappedPatient;
  open: boolean;
  user: any;
  onClose: VoidFunction;
};

const EditMode = ({ patient, open, user, onClose }: EditModeProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const [, { update }] = usePatient(patient.id!, { map: PatientWrapper });

  const EventSchema = Yup.object().shape({
    email: Yup.array(
      Yup.object({
        value: Yup.string()
          .required('Email is required')
          .email('Email must be a valid email address'),
      })
    ),
    phone: Yup.array(
      Yup.object({
        areaCode: Yup.string()
          .nullable()
          .transform((curr, orig) => orig === '' ? null : curr)
          .matches(/^[(]?[0-9]{3}[)]$/, "Area Code must be 3 numbers")
          .when('value', (value, schema) => {
            if (value) return schema.required('Area Code is required');
          }),
        value: Yup.string()
          .required('Phone is required')
          .matches(/^[0-9]{3}[-\s\.]?[0-9]{4,6}$/, "Phone Number must be 7 numbers"),
      })
    ),
  });

  const defaultValues = {
    email: patient.getEmails(),
    phone: patient.getPhoneNumbers()?.map((p) => {
      let valueClean: any = p?.value?.replace(/[^0-9]/g, '');
      const phoneModified = {
         ...p, 
         areaCode: `(${valueClean?.substring(0,3)})`, 
         value: valueClean?.substring(3,10) };
      return phoneModified;
    }),
  };

  const methods = useForm<FormValue>({
    resolver: yupResolver(EventSchema),
    defaultValues,
  });

  const {
    watch,
    setValue,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = methods;

  const mapFormToPatientTelecom = (data: Telecom[] | undefined): any => {
    const telecom = data?.map((item, index) => ({
      rank: index + 1,
      use: item.use,
      system: item.system,
      ...(item.areaCode ? 
        {value: `${item.areaCode} ${item.value}`} : 
        {value: `${item.value}`}),
      ...((item.period?.start || item.period?.end) && {
        period: {
          ...(item.period?.start && { start: item.period?.start }),
          ...(item.period?.end && { end: item.period?.end }),
        },
      }),
    }));

    return telecom;
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  const validateDateFields = (data: Telecom[]) => {
    let isValid = true;
    data.map(({ period, system }, index) => {
      const startDate = moment(period?.start ?? null);
      const endDate = moment(period?.end ?? null);
      if (startDate.isValid() && endDate.isValid()) {
        if (startDate.isAfter(endDate)) {

          // `${system}.${index}.period.start` as 'email' | 'phone'
          // TODO: fix with proper error type

          setError('email', {
            type: 'manual',
            message: "Start Date can't be greater than End Date",
          });
          isValid = false;
        }
      }
    });
    return isValid;
  };

  const onSubmit = async (data: FormValue) => {
    const emails = mapFormToPatientTelecom(data.email);
    const phones = mapFormToPatientTelecom(data.phone);
    if (!validateDateFields(emails) || !validateDateFields(phones)) return;

    try {
      await update(
        produce(patient!, (draft) => {
          draft.telecom = [...emails, ...phones];
        })
      );

      onClose();
      enqueueSnackbar('Patient was updated.');
    } catch {
      reset(defaultValues);
      onClose();
      enqueueSnackbar('Patient was not updated.', { variant: 'error' });
    }
  };

  const values = watch();

  const reorder = (list: any, startIndex: any, endIndex: any) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination) return;

    const list = source.droppableId === 'phone' ? values.phone : values.email;

    const items = reorder(list, result.source.index, result?.destination?.index);

    //@ts-ignore
    setValue(source.droppableId, items);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth="lg">
      <DialogTitle>Contact Details</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ m: 2 }}>
          <Grid container spacing={2} sx={{ p: 2 }}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Grid item xs={6}>
                <Droppable droppableId="email">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <Email user={user}/>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </Grid>
              <Grid item xs={6}>
                <Droppable droppableId="phone">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <Phone user={user}/>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </Grid>
            </DragDropContext>
          </Grid>
          <Stack spacing={2} alignItems="center">
            <DialogActions>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" color="info" onClick={handleClose}>
                Cancel
              </Button>
              {checkAclValidation({ 
                user, acl: getRelatedAcls(EditContactDetails)})
                && (
                <Button variant="contained" color="info" type="submit">
                  Submit
                </Button>
              )}
            </DialogActions>
          </Stack>
        </Card>
      </FormProvider>
    </Dialog>
  );
};

type DisplayModeProps = {
  patient: WrappedPatient;
};

const DisplayMode = ({ patient }: DisplayModeProps) => {
  const emails = patient.getEmails();
  const phones = patient.getPhoneNumbers();

  return (
    <Grid container spacing={3} sx={{ p: 1 }}>
      <Grid item xs={6}>
        <Typography variant="body2" sx={{ py: 1, color: 'text.secondary', fontWeight: 'bold' }}>
          Email
        </Typography>
        {emails.map((item) => (
          <Stack key={`em-${item.id}`} direction="row" sx={{ mb: 2 }}>
            <Typography variant="body2">{item.use}</Typography>

            <Box sx={{ flexGrow: 1, ml: 1 }}>
              <Typography variant="body2">{item.value}</Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {item.period?.start && `${format(new Date(item.period?.start), 'MMM yyyy')} to `}
                {item.period?.end && format(new Date(item.period?.end), 'MMM yyyy')}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Grid>

      <Grid item xs={6}>
        <Typography variant="body2" sx={{ py: 1, color: 'text.secondary', fontWeight: 'bold' }}>
          Phone
        </Typography>
        {phones.map((item) => (
          <Stack key={`ph-${item.id}`} direction="row" sx={{ mb: 2 }}>
            <Typography variant="body2">{item.use}</Typography>

            <Box sx={{ flexGrow: 1, ml: 1 }}>
              <Typography variant="body2">{item.value}</Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {item.period?.start && `${format(new Date(item.period?.start), 'MMM yyyy')} to `}
                {item.period?.end && format(new Date(item.period?.end), 'MMM yyyy')}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Grid>
    </Grid>
  );
};

type Props = {
  patient: WrappedPatient;
};

export default function ContactDetails({ patient }: Props) {

  const user = useAuth();
  const [editContact, setEditContact] = useState(false);

  const handleCloseEditContact = () => {
    setEditContact(false);
  };

  return (
    <>
      <Stack direction="row" style={{ display: "flex"}}>
        <Typography variant="button" sx={{ textTransform: 'uppercase', mt: 1 }}>
          Contact Details
        </Typography> 
          {checkAclValidation({ user, acl: getRelatedAcls(ViewContactDetails) }) && (
            <IconButton onClick={() => setEditContact(true)} sx={{ p: 0.5, ml: 1 }}>
              <Iconify icon={editContact ? 'eva:save-outline' : 'eva:edit-outline'} />
            </IconButton>
          )}
      </Stack>
      <DisplayMode patient={patient} />
      <EditMode patient={patient} open={editContact} user={user} onClose={handleCloseEditContact} />
    </>
  );
}
